import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Stack,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ReplayIcon from "@mui/icons-material/Replay";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { alpha } from "@mui/material/styles";
import { portalGetTicket, portalReopenTicket } from "../../api/clientPortal";
import ClientPortalMessageComposer from "./ClientPortalMessageComposer";
import ClientPortalMessageList from "./ClientPortalMessageList";
import ClientPortalTicketHeader from "./ClientPortalTicketHeader";
import { getClientPortalErrorMessage } from "./clientPortalErrorMessage";
import { usePortalTicketMessages } from "./hooks/usePortalTicketMessages";
import { executeOptimisticPortalSend } from "./utils/portalOptimisticSend";

export default function ClientPortalTicketDetailPage() {
  const { id } = useParams();
  const { q } = useOutletContext();
  const navigate = useNavigate();
  const [scrollToBottomNonce, setScrollToBottomNonce] = useState(0);

  const [ticket, setTicket] = useState(null);
  const [ticketLoading, setTicketLoading] = useState(true);
  const [reopening, setReopening] = useState(false);
  const [error, setError] = useState(null);

  const {
    messages,
    setMessages,
    hasMore,
    isLoading: messagesLoading,
    isLoadingMore,
    loadMessages,
    loadMore,
    syncLatest,
  } = usePortalTicketMessages(id);

  const isClosed = Boolean(ticket?.isClosed);
  const canReply = ticket?.canReply !== false && !isClosed;
  const canReopen = Boolean(ticket?.canReopen);
  const loading = ticketLoading || (messagesLoading && messages.length === 0);

  const loadTicket = useCallback(async () => {
    setTicketLoading(true);
    setError(null);
    try {
      const t = await portalGetTicket(id);
      setTicket(t?.ticket || t);
    } catch (e) {
      setError(getClientPortalErrorMessage(e, { context: "ticketDetail" }));
    } finally {
      setTicketLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadTicket();
  }, [loadTicket]);

  const handleComposerSend = useCallback(
    async (payload) => {
      if (!canReply) return;
      setScrollToBottomNonce((n) => n + 1);
      return executeOptimisticPortalSend({
        ticketId: id,
        payload,
        setMessages,
        syncMessages: syncLatest,
      });
    },
    [canReply, id, setMessages, syncLatest]
  );

  const handleQuickReply = useCallback(
    async (_messageId, label) => {
      if (!canReply || !label) return;
      const text = String(label).trim();
      if (!text) return;
      setScrollToBottomNonce((n) => n + 1);
      return executeOptimisticPortalSend({
        ticketId: id,
        payload: { type: "text", message: text },
        setMessages,
        syncMessages: syncLatest,
      });
    },
    [canReply, id, setMessages, syncLatest]
  );

  const handleReopen = async () => {
    setReopening(true);
    setError(null);
    try {
      const data = await portalReopenTicket(id);
      setTicket(data?.ticket || data);
      await Promise.all([loadTicket(), loadMessages()]);
    } catch (err) {
      setError(getClientPortalErrorMessage(err, { context: "reopen" }));
    } finally {
      setReopening(false);
    }
  };

  const handleBack = useCallback(() => {
    navigate(`/portal-cliente/app${q}`);
  }, [navigate, q]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1, py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        height: "100%",
        bgcolor: "background.default",
      }}
    >
      <ClientPortalTicketHeader ticket={ticket} isClosed={isClosed} onBack={handleBack} />

      {error ? (
        <Alert severity="error" sx={{ flexShrink: 0, borderRadius: 0 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      <ClientPortalMessageList
        ticketId={id}
        messages={messages}
        hasMore={hasMore}
        isLoading={messagesLoading}
        isLoadingMore={isLoadingMore}
        onLoadMore={loadMore}
        scrollToBottomNonce={scrollToBottomNonce}
        onQuickReply={handleQuickReply}
        canReply={canReply}
      />

      <Box
        sx={{
          flexShrink: 0,
          px: { xs: 1, sm: 1.5 },
          py: { xs: 1, sm: 1.25 },
          pb: { xs: "max(10px, env(safe-area-inset-bottom, 0px))", sm: 1.25 },
          borderTop: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
          boxShadow: (t) => `0 -4px 16px ${alpha(t.palette.common.black, 0.04)}`,
        }}
      >
        {isClosed ? (
          <Alert severity="info" sx={{ borderRadius: 2, m: 0 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
              Esta solicitação foi encerrada
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
              Não é possível enviar novas mensagens aqui. Se o assunto for o mesmo, você pode reabrir esta
              solicitação. Para um novo problema, abra outra solicitação.
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              {canReopen ? (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<ReplayIcon />}
                  disabled={reopening}
                  onClick={handleReopen}
                >
                  {reopening ? "Reabrindo…" : "Reabrir conversa"}
                </Button>
              ) : null}
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => navigate(`/portal-cliente/app/new${q}`)}
              >
                Nova solicitação
              </Button>
            </Stack>
          </Alert>
        ) : (
          <ClientPortalMessageComposer disabled={!canReply} onSend={handleComposerSend} />
        )}
      </Box>
    </Box>
  );
}
